import { useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert } from '@vnetwork/ui/feedback';
import { CentralMetric, resolveTimeRange, type TimeRange } from '@/api/manticore';
import {
  RangeControl,
  RefreshButton,
  BarCard,
  CardGrid,
  Stack,
} from '../../_shared/AnalyticsKit';
import { formatBytes } from '../../_shared/analytics-format';
import { useScopedSeries, mapToBars, LOCATION_PERIODS } from '../analytics-series';

/** Viewer location view — request and volume share by country. */
export function ViewerLocationTab({ projectIds }: { projectIds?: string }) {
  const [range, setRange] = useState<TimeRange>({ kind: 'preset', period: '24h' });
  const qc = useQueryClient();
  const resolved = useMemo(() => resolveTimeRange(range), [range]);
  const common = {
    domainId: 'all',
    projectIds,
    period: range.kind === 'preset' ? range.period : '24h',
    range: range.kind === 'custom' ? resolved : undefined,
  } as const;

  const geoReqQ = useScopedSeries({ ...common, metric: CentralMetric.GeoRequests, breakdown: 'country' });
  const geoVolQ = useScopedSeries({ ...common, metric: CentralMetric.GeoVolume, breakdown: 'country' });

  const reqBars = useMemo(() => mapToBars(geoReqQ.data?.by_dimension, { max: 12 }), [geoReqQ.data]);
  const volBars = useMemo(
    () => mapToBars(geoVolQ.data?.by_dimension, { max: 12, display: formatBytes }),
    [geoVolQ.data],
  );

  return (
    <Stack>
      <Alert
        tone="info"
        icon={<Info size={16} />}
        title="Viewer-by-location data updates on a daily (D+1) cycle"
      >
        Location breakdowns are aggregated once per day, so figures typically lag
        the present by up to 24 hours. A gap for the most recent day is expected,
        not a data error.
      </Alert>

      {/* Controls row — standalone flex row so it wraps on narrow viewports.
          Section.actions uses flex-shrink:0 which prevents wrap; this avoids that. */}
      <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
        <RangeControl options={LOCATION_PERIODS} value={range} onChange={setRange} />
        <RefreshButton onRefresh={() => qc.invalidateQueries({ queryKey: ['cdn', 'central-analytics'] })} />
      </div>

      <CardGrid min={320}>
        <BarCard title="Top countries by requests" items={reqBars} loading={geoReqQ.isLoading} />
        <BarCard title="Top countries by volume" items={volBars} loading={geoVolQ.isLoading} />
      </CardGrid>
    </Stack>
  );
}
